import React, { useCallback, useEffect, useState } from "react";
import {
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Loader2,
  Filter,
  ListTree,
} from "lucide-react";
import { ApplicationAuditLogResponseDTO } from "../Types/ApplicationLogs";
import { searchApplicationAuditLogs } from "../Services/ApplicationLogService";
import { formatDateTime } from "../../Shared/Utils/HelperUtils";
import Pagination from "../../Shared/Pagination/Pagination";
import { Badge, statusStyles, statusIcon, eventTypeStyle } from "../Utils/LogColors";

interface Filters {
  text: string;
  eventType: string;
  entityType: string;
  entityId: string;
  performedBy: string;
  performedByRole: string;
  status: string;
  fromDate: string;
  toDate: string;
}

const EMPTY_FILTERS: Filters = {
  text: "",
  eventType: "",
  entityType: "",
  entityId: "",
  performedBy: "",
  performedByRole: "",
  status: "",
  fromDate: "",
  toDate: "",
};

const ROLES = ["SUPER_ADMIN", "ADMIN", "RECRUITER", "COMPANY_ADMIN", "CANDIDATE", "SYSTEM"];
const STATUSES = ["SUCCESS", "FAILED", "PENDING", "WARN", "PARTIAL_SUCCESS", "SKIPPED", "CANCELLED"];

const selectClass =
  "w-full bg-[#0f172a] border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300 focus:border-blue-500 outline-none";
const inputClass =
  "w-full bg-[#0f172a] border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300 focus:border-blue-500 outline-none";

const LogViewerComponentForApplicationLogs: React.FC = () => {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [logsData, setLogsData] = useState<ApplicationAuditLogResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
    hasNext: false,
    hasPrevious: false,
  });

  const buildPayload = useCallback(() => {
    const payload: any = {
      eventType: filters.eventType || undefined,
      entityType: filters.entityType || undefined,
      entityId: filters.entityId || undefined,
      performedBy: filters.performedBy || undefined,
      performedByRole: filters.performedByRole || undefined,
      status: filters.status || undefined,
      text: filters.text || undefined,
      fromDate: filters.fromDate ? new Date(filters.fromDate).toISOString() : undefined,
      toDate: filters.toDate ? new Date(filters.toDate).toISOString() : undefined,
    };
    return Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== undefined && v !== "")
    );
  }, [filters]);

  const runSearch = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      const response = await searchApplicationAuditLogs(buildPayload(), targetPage, 50);
      setLoading(false);

      if (response.success && response.data) {
        setLogsData(response.data.content);
        setPagination({
          page: response.data.page,
          size: response.data.size,
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          first: response.data.first,
          last: response.data.last,
          hasNext: response.data.hasNext,
          hasPrevious: response.data.hasPrevious,
        });
      }
    },
    [buildPayload]
  );

  useEffect(() => {
    runSearch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    runSearch(0);
  };

  const handlePageChange = (p: number | ((prev: number) => number)) => {
    if (typeof p === "number") {
      runSearch(p);
    }
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#0f172a] text-slate-300 font-sans">
      {/* Header / Search */}
      <div className="flex-none border-b border-slate-800 px-4 py-3 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-wide">
          <ListTree size={16} /> Application Logs
        </span>

        <div className="flex-1 min-w-[220px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={15} />
          <input
            type="text"
            value={filters.text}
            onChange={(e) => setFilters({ ...filters, text: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && runSearch(0)}
            placeholder="Search event, entity, actor, description..."
            className="w-full bg-[#0f172a] border border-slate-700 rounded-md py-2 pl-9 pr-3 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </div>

        <button
          onClick={() => runSearch(0)}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-xs font-semibold"
        >
          Run
        </button>

        <button
          onClick={resetFilters}
          className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 hover:border-slate-600 text-slate-300 px-3 py-2 rounded-md text-xs font-semibold"
        >
          <RefreshCw size={13} /> Clear
        </button>

        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`md:hidden flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-all ${
            showFilters || activeFilterCount > 0
              ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
              : "bg-slate-800 border-slate-700 text-slate-300"
          }`}
        >
          <Filter size={13} /> Filters
          {activeFilterCount > 0 && (
            <span className="px-1.5 rounded-full bg-blue-600 text-white text-[10px]">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown
            size={13}
            className={`transition-transform ${showFilters ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Filter bar */}
      <div
        className={`flex-none border-b border-slate-800 px-4 py-2.5 ${
          showFilters ? "grid" : "hidden"
        } md:grid grid-cols-2 md:grid-cols-4 xl:grid-cols-9 gap-2`}
      >
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Event type</label>
          <input
            className={inputClass}
            value={filters.eventType}
            placeholder="e.g. PAYMENT_SUCCESS"
            onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Entity type</label>
          <input
            className={inputClass}
            value={filters.entityType}
            placeholder="e.g. RECRUITER"
            onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Entity id</label>
          <input
            className={inputClass}
            value={filters.entityId}
            placeholder="entity id"
            onChange={(e) => setFilters({ ...filters, entityId: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Performed by</label>
          <input
            className={inputClass}
            value={filters.performedBy}
            placeholder="name / email"
            onChange={(e) => setFilters({ ...filters, performedBy: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Role</label>
          <select
            className={selectClass}
            value={filters.performedByRole}
            onChange={(e) => setFilters({ ...filters, performedByRole: e.target.value })}
          >
            <option value="">All</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Status</label>
          <select
            className={selectClass}
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">From</label>
          <input
            type="datetime-local"
            className={inputClass}
            value={filters.fromDate}
            onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">To</label>
          <input
            type="datetime-local"
            className={inputClass}
            value={filters.toDate}
            onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
          />
        </div>
        <div className="flex items-end">
          <div className="text-[10px] text-slate-500 py-1.5">
            {logsData.length} on page · {pagination.totalElements} total
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-auto min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-500">
            <Loader2 size={22} className="animate-spin mr-2" /> Loading application logs...
          </div>
        ) : logsData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-600">
            <Filter size={28} className="mr-2 opacity-40" />
            {activeFilterCount > 0 ? "No events match the current filters." : "No application logs recorded yet."}
          </div>
        ) : (
          <div className="p-3">
            {/* Desktop table */}
            <table className="hidden md:table w-full text-left border-collapse">
              <thead>
                <tr className="text-slate-500 text-[10px] uppercase border-b border-slate-800">
                  <th className="pb-2 pl-2 w-8"></th>
                  <th className="pb-2 font-semibold">Time</th>
                  <th className="pb-2 font-semibold text-center">Status</th>
                  <th className="pb-2 font-semibold">Event</th>
                  <th className="pb-2 font-semibold">Entity</th>
                  <th className="pb-2 font-semibold">Actor</th>
                  <th className="pb-2 font-semibold">Role</th>
                  <th className="pb-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="text-xs font-mono">
                {logsData.map((log) => (
                  <AppAuditRow
                    key={log.id}
                    log={log}
                    isExpanded={expandedLog === log.id}
                    onToggle={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                  />
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="md:hidden flex flex-col divide-y divide-slate-800">
              {logsData.map((log) => (
                <MobileAppAuditCard
                  key={log.id}
                  log={log}
                  isExpanded={expandedLog === log.id}
                  onToggle={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                />
              ))}
            </div>

            <Pagination
              page={pagination.page}
              size={pagination.size || 50}
              totalElements={pagination.totalElements}
              totalPages={pagination.totalPages}
              first={pagination.first}
              last={pagination.last}
              hasNext={pagination.hasNext}
              hasPrevious={pagination.hasPrevious}
              setPage={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const AppAuditRow: React.FC<{
  log: ApplicationAuditLogResponseDTO;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ log, isExpanded, onToggle }) => {
  const status = log.status || "SUCCESS";

  return (
    <React.Fragment>
      <tr className="border-b border-slate-800/60 hover:bg-slate-800/20 cursor-pointer transition-colors">
        <td className="py-2 pl-2" onClick={onToggle}>
          {isExpanded ? (
            <ChevronDown size={14} className="text-slate-400" />
          ) : (
            <ChevronRight size={14} className="text-slate-500" />
          )}
        </td>
        <td className="py-2 text-slate-400 whitespace-nowrap" onClick={onToggle}>
          {formatDateTime(log.createdAt, "dateTimeWithSeconds")}
        </td>
        <td className="py-2 text-center" onClick={onToggle}>
          <Badge label={status} className={statusStyles[status] || statusStyles.SKIPPED} icon={statusIcon(status)} />
        </td>
        <td className="py-2" onClick={onToggle}>
          <Badge label={log.eventType || "-"} className={eventTypeStyle(log.eventType)} />
        </td>
        <td className="py-2 text-[10px] text-slate-500 max-w-[160px] truncate" title={log.entityId} onClick={onToggle}>
          {log.entityType ? `${log.entityType} ${log.entityId || ""}`.trim() : "-"}
        </td>
        <td className="py-2 text-slate-300 max-w-[140px] truncate" title={log.performedBy} onClick={onToggle}>
          {log.performedBy || "-"}
        </td>
        <td className="py-2 text-slate-400" onClick={onToggle}>
          {log.performedByRole || "-"}
        </td>
        <td className="py-2 text-slate-400 max-w-[340px] truncate" title={log.description} onClick={onToggle}>
          {log.description || "-"}
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-slate-900/60">
          <td colSpan={8} className="px-4 py-3">
            <div className="grid gap-3 text-xs">
              {log.description && (
                <div>
                  <div className="text-[9px] uppercase text-slate-500 mb-1">Description</div>
                  <div className="bg-slate-950 border border-slate-800 rounded-md p-2 text-slate-300 whitespace-pre-wrap break-words">
                    {log.description}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-slate-400">
                <KV k="Event id" v={log.id} />
                <KV k="Entity" v={log.entityType ? `${log.entityType} ${log.entityId || ""}`.trim() : undefined} />
                <KV k="Actor" v={log.performedBy} />
                <KV k="Role" v={log.performedByRole} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const MobileAppAuditCard: React.FC<{
  log: ApplicationAuditLogResponseDTO;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ log, isExpanded, onToggle }) => {
  const status = log.status || "SUCCESS";
  return (
    <div className="py-3 cursor-pointer" onClick={onToggle}>
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <span className="text-blue-400 text-[11px] font-mono">
          {formatDateTime(log.createdAt, "dateTimeWithSeconds")}
        </span>
        <Badge label={status} className={statusStyles[status] || statusStyles.SKIPPED} icon={statusIcon(status)} />
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Badge label={log.eventType || "-"} className={eventTypeStyle(log.eventType)} />
        {log.performedByRole && (
          <span className="text-[10px] text-slate-500 uppercase">{log.performedByRole}</span>
        )}
      </div>
      <p className="text-slate-400 text-xs truncate">{log.performedBy ? `${log.performedBy} — ` : ""}{log.description}</p>
      {isExpanded && (
        <div className="mt-3 bg-slate-950 border border-slate-800 rounded-md p-2 text-[11px] text-slate-300 whitespace-pre-wrap break-words">
          <div className="text-[9px] uppercase text-slate-500 mb-1">Description</div>
          {log.description || "-"}
        </div>
      )}
    </div>
  );
};

const KV: React.FC<{ k: string; v?: string }> = ({ k, v }) => (
  <div className="min-w-0">
    <div className="text-[9px] uppercase text-slate-600">{k}</div>
    <div className="truncate text-slate-300">{v || "-"}</div>
  </div>
);

export default LogViewerComponentForApplicationLogs;